import config from 'config';

export type VegaLiteFloatSeriesLimits = {
  maxRuns: number;
  maxY: number;
  maxSeries: number;
  maxDependencies: number;
  maxExpressionInputs: number;
  maxExpressionBytes: number;
  maxConcurrency: number;
  deadlineMs: number;
  maxPoints: number;
  maxRows: number;
  maxUniqueX: number;
};

export type VegaLiteEvaluationLimits = {
  deadlineMs: number;
  maxCells: number;
  maxConcurrency: number;
  maxGroupByFields: number;
  maxPages: number;
  maxPreparedRows: number;
  maxRecordMetrics: number;
  maxSeries: number;
  maxSources: number;
  pageSize: number;
};

type VegaLiteLimitSetting = {
  configKey: string;
  envVar: string;
  min?: number;
  max?: number;
};

export const VEGA_LITE_FLOAT_SERIES_DEFAULT_LIMITS: Readonly<VegaLiteFloatSeriesLimits> =
  Object.freeze({
    maxRuns: 10,
    maxY: 20,
    maxSeries: 100,
    maxDependencies: 64,
    maxExpressionInputs: 16,
    maxExpressionBytes: 4096,
    maxConcurrency: 2,
    deadlineMs: 30_000,
    maxPoints: 50_000,
    maxRows: 10_000,
    maxUniqueX: 1000,
  });

export const VEGA_LITE_EVALUATION_DEFAULT_LIMITS: Readonly<VegaLiteEvaluationLimits> =
  Object.freeze({
    deadlineMs: 30_000,
    maxCells: 50_000,
    maxConcurrency: 2,
    maxGroupByFields: 6,
    maxPages: 10,
    maxPreparedRows: 10_000,
    maxRecordMetrics: 20,
    maxSeries: 100,
    maxSources: 10,
    // Fixed at the server maximum pageSize of a records request; no deployment setting changes it.
    pageSize: 10_000,
  });

// setTimeout fires at once for a delay above 2^31 - 1 ms, so a longer deadline would end every load.
const MAX_TIMER_DELAY_MS = 2_147_483_647;
// The series view request carries maxUniqueX as a Java int.
const MAX_JAVA_INT = 2_147_483_647;

/**
 * Each limit except the evaluation `pageSize` can be overridden by a frontend container env var.
 * `docker/backend_settings.sh` copies a set env var verbatim into the matching `config` key; the
 * bounds reject values the backend refuses on every request or that a timer cannot hold.
 */
export const VEGA_LITE_FLOAT_SERIES_LIMIT_SETTINGS = {
  maxRuns: { configKey: 'vegaLiteFloatSeriesMaxRuns', envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_RUNS' },
  maxY: { configKey: 'vegaLiteFloatSeriesMaxY', envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_Y' },
  maxSeries: {
    configKey: 'vegaLiteFloatSeriesMaxSeries',
    envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_SERIES',
  },
  maxDependencies: {
    configKey: 'vegaLiteFloatSeriesMaxDependencies',
    envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_DEPENDENCIES',
  },
  maxExpressionInputs: {
    configKey: 'vegaLiteFloatSeriesMaxExpressionInputs',
    envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_EXPRESSION_INPUTS',
  },
  maxExpressionBytes: {
    configKey: 'vegaLiteFloatSeriesMaxExpressionBytes',
    envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_EXPRESSION_BYTES',
  },
  maxConcurrency: {
    configKey: 'vegaLiteFloatSeriesMaxConcurrency',
    envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_CONCURRENCY',
  },
  deadlineMs: {
    configKey: 'vegaLiteFloatSeriesDeadlineMs',
    envVar: 'VEGA_LITE_FLOAT_SERIES_DEADLINE_MS',
    max: MAX_TIMER_DELAY_MS,
  },
  maxPoints: {
    configKey: 'vegaLiteFloatSeriesMaxPoints',
    envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_POINTS',
  },
  maxRows: { configKey: 'vegaLiteFloatSeriesMaxRows', envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_ROWS' },
  // The series view endpoint rejects maxUniqueX below 2.
  maxUniqueX: {
    configKey: 'vegaLiteFloatSeriesMaxUniqueX',
    envVar: 'VEGA_LITE_FLOAT_SERIES_MAX_UNIQUE_X',
    min: 2,
    max: MAX_JAVA_INT,
  },
} as const satisfies Record<keyof VegaLiteFloatSeriesLimits, VegaLiteLimitSetting>;

export const VEGA_LITE_EVALUATION_LIMIT_SETTINGS = {
  deadlineMs: {
    configKey: 'vegaLiteEvaluationDeadlineMs',
    envVar: 'VEGA_LITE_EVALUATION_DEADLINE_MS',
    max: MAX_TIMER_DELAY_MS,
  },
  maxCells: { configKey: 'vegaLiteEvaluationMaxCells', envVar: 'VEGA_LITE_EVALUATION_MAX_CELLS' },
  maxConcurrency: {
    configKey: 'vegaLiteEvaluationMaxConcurrency',
    envVar: 'VEGA_LITE_EVALUATION_MAX_CONCURRENCY',
  },
  maxGroupByFields: {
    configKey: 'vegaLiteEvaluationMaxGroupByFields',
    envVar: 'VEGA_LITE_EVALUATION_MAX_GROUP_BY_FIELDS',
  },
  maxPages: { configKey: 'vegaLiteEvaluationMaxPages', envVar: 'VEGA_LITE_EVALUATION_MAX_PAGES' },
  maxPreparedRows: {
    configKey: 'vegaLiteEvaluationMaxPreparedRows',
    envVar: 'VEGA_LITE_EVALUATION_MAX_PREPARED_ROWS',
  },
  maxRecordMetrics: {
    configKey: 'vegaLiteEvaluationMaxRecordMetrics',
    envVar: 'VEGA_LITE_EVALUATION_MAX_RECORD_METRICS',
  },
  maxSeries: {
    configKey: 'vegaLiteEvaluationMaxSeries',
    envVar: 'VEGA_LITE_EVALUATION_MAX_SERIES',
  },
  // A records batch request accepts at most 1000 sources.
  maxSources: {
    configKey: 'vegaLiteEvaluationMaxSources',
    envVar: 'VEGA_LITE_EVALUATION_MAX_SOURCES',
    max: 1000,
  },
} as const satisfies Record<
  Exclude<keyof VegaLiteEvaluationLimits, 'pageSize'>,
  VegaLiteLimitSetting
>;

type SettingsConfigKey<Settings extends Record<string, VegaLiteLimitSetting>> =
  Settings[keyof Settings]['configKey'];

export type VegaLiteWidgetLimitsConfigSource = {
  readonly [Key in
    | SettingsConfigKey<typeof VEGA_LITE_FLOAT_SERIES_LIMIT_SETTINGS>
    | SettingsConfigKey<typeof VEGA_LITE_EVALUATION_LIMIT_SETTINGS>]?: unknown;
};

const DECIMAL_DIGITS = /^[0-9]+$/;

function getLimitBounds(setting: VegaLiteLimitSetting): { min: number; max: number } {
  return { min: setting.min ?? 1, max: setting.max ?? Number.MAX_SAFE_INTEGER };
}

function toVegaLiteLimit(raw: unknown, setting: VegaLiteLimitSetting): number | undefined {
  const { min, max } = getLimitBounds(setting);
  const value = typeof raw === 'string' && DECIMAL_DIGITS.test(raw) ? Number(raw) : Number.NaN;

  return Number.isSafeInteger(value) && value >= min && value <= max ? value : undefined;
}

/**
 * Accepts only decimal digits that form a safe integer within the setting bounds (at least 1).
 * Anything else falls back to the default with a warning, so a malformed deployment value can
 * never disable a limit.
 */
export function parseVegaLiteLimit(
  raw: unknown,
  setting: VegaLiteLimitSetting,
  defaultValue: number,
): number {
  if (raw === undefined) {
    return defaultValue;
  }

  const value = toVegaLiteLimit(raw, setting);

  if (value !== undefined) {
    return value;
  }

  const { min, max } = getLimitBounds(setting);
  // eslint-disable-next-line no-console
  console.warn(
    `${setting.envVar} must be an integer from ${min} to ${max} written in decimal digits; using the default ${defaultValue}.`,
  );
  return defaultValue;
}

/** Formats a limit for messages with comma thousands separators, for example "50,000". */
export function formatVegaLiteLimit(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function readLimits<Limits extends Record<string, number>, Key extends keyof Limits>(
  defaults: Readonly<Limits>,
  settings: Record<Key, VegaLiteLimitSetting>,
  source: object,
): Readonly<Limits> {
  const values = source as Record<string, unknown>;
  const limits = { ...defaults } as Limits;

  (Object.keys(settings) as Key[]).forEach((key) => {
    const setting = settings[key];
    limits[key] = parseVegaLiteLimit(
      values[setting.configKey],
      setting,
      defaults[key],
    ) as Limits[Key];
  });

  return Object.freeze(limits);
}

export function readVegaLiteFloatSeriesLimits(
  source: VegaLiteWidgetLimitsConfigSource,
): Readonly<VegaLiteFloatSeriesLimits> {
  return readLimits(
    VEGA_LITE_FLOAT_SERIES_DEFAULT_LIMITS,
    VEGA_LITE_FLOAT_SERIES_LIMIT_SETTINGS,
    source,
  );
}

export function readVegaLiteEvaluationLimits(
  source: VegaLiteWidgetLimitsConfigSource,
): Readonly<VegaLiteEvaluationLimits> {
  return readLimits(
    VEGA_LITE_EVALUATION_DEFAULT_LIMITS,
    VEGA_LITE_EVALUATION_LIMIT_SETTINGS,
    source,
  );
}

/** Effective limits for this deployment, resolved once from `config`. */
export const VEGA_LITE_FLOAT_SERIES_LIMITS = readVegaLiteFloatSeriesLimits(config);
export const VEGA_LITE_EVALUATION_LIMITS = readVegaLiteEvaluationLimits(config);

export type VegaLiteWidgetLimitStatus = {
  envVar: string;
  /** The limit the widgets use. */
  value: number;
  defaultValue: number;
} & ({ override: 'none' } | { override: 'applied' | 'rejected'; raw: unknown });

/**
 * Describes each limit by its `config` key for the admin panel: the effective value, the default
 * and the raw override, if any, marked as rejected when the parser kept the default. Nothing is
 * parsed again, so no warning is logged.
 */
export function describeVegaLiteWidgetLimits(
  source: VegaLiteWidgetLimitsConfigSource,
  floatSeriesLimits: Readonly<VegaLiteFloatSeriesLimits> = VEGA_LITE_FLOAT_SERIES_LIMITS,
  evaluationLimits: Readonly<VegaLiteEvaluationLimits> = VEGA_LITE_EVALUATION_LIMITS,
): ReadonlyMap<string, VegaLiteWidgetLimitStatus> {
  const values: Record<string, unknown> = source;
  const statuses = new Map<string, VegaLiteWidgetLimitStatus>();

  const describe = <Limits extends Record<string, number>, Key extends keyof Limits>(
    settings: Record<Key, VegaLiteLimitSetting>,
    limits: Readonly<Limits>,
    defaults: Readonly<Limits>,
  ) => {
    (Object.keys(settings) as Key[]).forEach((key) => {
      const setting = settings[key];
      const raw = values[setting.configKey];
      const limit = { envVar: setting.envVar, value: limits[key], defaultValue: defaults[key] };
      statuses.set(
        setting.configKey,
        raw === undefined
          ? { ...limit, override: 'none' }
          : {
              ...limit,
              override: toVegaLiteLimit(raw, setting) === undefined ? 'rejected' : 'applied',
              raw,
            },
      );
    });
  };

  describe(
    VEGA_LITE_FLOAT_SERIES_LIMIT_SETTINGS,
    floatSeriesLimits,
    VEGA_LITE_FLOAT_SERIES_DEFAULT_LIMITS,
  );
  describe(
    VEGA_LITE_EVALUATION_LIMIT_SETTINGS,
    evaluationLimits,
    VEGA_LITE_EVALUATION_DEFAULT_LIMITS,
  );

  return statuses;
}

export function resolveVegaLiteFloatSeriesLimits(
  overrides?: Partial<VegaLiteFloatSeriesLimits>,
): VegaLiteFloatSeriesLimits {
  return { ...VEGA_LITE_FLOAT_SERIES_LIMITS, ...overrides };
}

export function resolveVegaLiteEvaluationLimits(
  overrides?: Partial<VegaLiteEvaluationLimits>,
): VegaLiteEvaluationLimits {
  return { ...VEGA_LITE_EVALUATION_LIMITS, ...overrides };
}
